import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import FamilyApi from "../../services/FamilyApi";
import { useToast } from "../../context/ToastContext";
import { classNames } from "primereact/utils";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const EditFormFamily: React.FC = () => {
  const { t: translate } = useTranslation("form");
  const [nome, setNome] = useState("");
  const [description, setDescricao] = useState("");
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { displayToast } = useToast();
  const [errors, setErrors] = useState<Record<string, string>>({
    description: "",
  });
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const nomeRef = useRef<any>(null);
  const descriptionRef = useRef<any>(null);
  const codeRef = useRef<any>(null);

  useEffect(() => {
    FamilyApi.GetById(id)
      .then((response) => {
        const item = response?.data || null;
        if (!item) throw new Error(translate("errorLoadingFamily"));
        setNome(item.name);
        setDescricao(item.description);
        setCode(String(item.code));
      })
      .catch(() => {
        displayToast("error", translate("error"), translate("errorLoadingFamily"), false, 3000);
      });
  }, [id, displayToast, translate]);

  const validate = (): boolean => {
    const newErr: Record<string, string> = {
      description: "",
      nome: "",
    };

    if (!nome.trim()) newErr.nome = translate("nameRequired");
    else if (nome.length > 50) newErr.nome = translate("NameExceptionCharacters");

    if (!description.trim()) newErr.description = translate("DescriptionRequired");
    else if (description.length > 600) newErr.description = translate("DescriptionExceptionCharacters");

    setErrors(newErr);
    return Object.values(newErr).every((e) => !e);
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    try {
      if (!validate()) {
        if (!nome.trim()) nomeRef.current?.focus();
        else descriptionRef.current?.focus();
        return;
      }
      if (!id) {
        displayToast("error", translate("error"), translate("errorUpdatingFamily"), false, 3000);
        return;
      }
      const payload = {
        id,
        name: nome,
        description: description,
      };
      await FamilyApi.UpdateFamily(payload);
      displayToast("success", translate("Success"), translate("FamilyUpdated"), false, 3000);
      navigate("/families");
    } catch (err: any) {
      const errorMessage = err?.data?.data || translate("errorUpdatingFamily");
      displayToast("error", translate("error"), errorMessage, false, 3000);
    }
  };

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  return (
    <>
      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/families")}
      />
      <FormCard
        title={translate("editFamily")}
        icon="edit"
        submitButtonLabel={translate("save")}
        cancelButtonLabel={translate("Cancel")}
        onSubmit={handleSave}
        onCancel={handleCancel}
        numColumns={12}
      >
      <div style={{ gridColumn: "span 12" }}>
        <Input
          ref={nomeRef}
          type="text"
          label={translate("Name")}
          value={nome}
          required
          placeholder={`${translate("Name")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.nome })}
          onChange={(val: any) => {
            setNome(val);
            setErrors(prev => ({ ...prev, nome: "" }));
          }}
        />
        {errors.nome && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
            {errors.nome}
          </small>
        )}
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <Input
          ref={descriptionRef}
          type="textArea"
          label={translate("description")}
          value={description}
          required
          rows={3}
          placeholder={`${translate("description")} *`}
          className={classNames("w-full", { "p-invalid border-red": errors.description })}
          onChange={(val: any) => {
            setDescricao(val);
            setErrors(prev => ({ ...prev, description: "" }));
          }}
        />
        {errors.description && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
            {errors.description}
          </small>
        )}
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          ref={codeRef}
          type="text"
          label={translate("code")}
          value={code}
          required
          disabled={!!id}
          placeholder={translate("code")}
          className={classNames("w-full", { "p-invalid border-red": errors.code })}
          onChange={(val: any) => setCode(val)}
        />
        {errors.code && (
          <small className="p-error" style={{ color: "var(--color-error-500)", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
            {errors.code}
          </small>
        )}
      </div>
    </FormCard>
    </>
  );
};

export default EditFormFamily;
